/**
 * SessionDetailPage — cinematic interview replay
 * Design: glassmorphism stage, no tabs, unified playback control
 * Progress bar: proportional section widths based on video durations,
 *               real-time progress from the longest video, auto-advance.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  getAdminSession,
  type AdminSessionDetail,
  type SessionSection,
} from '@/api/interview'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'

// ─────────────────────────────────────────────────────────────────────────────
// Design tokens
// ─────────────────────────────────────────────────────────────────────────────

const T = {
  font:     "'DM Sans', 'Roboto', sans-serif",
  accent:   '#00adef',
  text:     'rgba(255,255,255,0.95)',
  muted:    'rgba(255,255,255,0.60)',
  subtle:   'rgba(255,255,255,0.38)',
  rLg:      '22px',
  blur:     'blur(24px)',
  shadow:   '0 12px 48px rgba(0,0,0,0.35), 0 4px 16px rgba(0,0,0,0.2)',
} as const

const glassCard: React.CSSProperties = {
  background:           'rgba(0,0,0,0.22)',
  backdropFilter:       'blur(16px)',
  WebkitBackdropFilter: 'blur(16px)',
  border:               '1px solid rgba(255,255,255,0.07)',
  borderRadius:         T.rLg,
  padding:              '24px 26px',
}

const glassLabel: React.CSSProperties = {
  fontSize:      '10px',
  fontWeight:    600,
  textTransform: 'uppercase',
  letterSpacing: '1.2px',
  color:         T.subtle,
  marginBottom:  '14px',
  fontFamily:    T.font,
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function fmtTime(secs: number): string {
  if (!isFinite(secs) || isNaN(secs) || secs < 0) return '0:00'
  const m = Math.floor(secs / 60)
  const s = Math.floor(secs % 60)
  return `${m}:${s.toString().padStart(2, '0')}`
}

// Per-section raw durations: user and avatar video duration in seconds
type SectionDurs = Record<string, { user: number; avatar: number }>

// ─────────────────────────────────────────────────────────────────────────────
// Root component
// ─────────────────────────────────────────────────────────────────────────────

export function SessionDetailPage() {
  const navigate = useNavigate()
  const { id }   = useParams<{ id: string }>()

  const [session,       setSession]       = useState<AdminSessionDetail | null>(null)
  const [loading,       setLoading]       = useState(true)
  const [error,         setError]         = useState(false)
  const [activeSection, setActiveSection] = useState<string | null>(null)
  const [isPlaying,     setIsPlaying]     = useState(false)

  // Real-time playback tracking
  const [sectionDurs, setSectionDurs] = useState<SectionDurs>({})
  const [currentTime,  setCurrentTime] = useState(0)

  const candidateVideoRef = useRef<HTMLVideoElement>(null)
  const avatarVideoRef    = useRef<HTMLVideoElement>(null)

  // Stable refs for event handlers (avoids stale closures)
  const sectionsRef    = useRef<SessionSection[]>([])
  const activeIdxRef   = useRef(0)
  // true → the user video is the longer one (or equal); false → avatar is longer
  const primaryIsUserRef = useRef(true)
  // Guards against double-advance when both videos fire `ended` nearly simultaneously
  const advancedKeyRef   = useRef<string | null>(null)
  // When set, the next section-load should auto-play (auto-advance path)
  const autoPlayNextRef  = useRef(false)

  // Keep stable refs in sync
  useEffect(() => { sectionsRef.current  = session?.sections ?? [] }, [session])
  useEffect(() => { activeIdxRef.current = session?.sections.findIndex(s => s.key === activeSection) ?? 0 },
    [session, activeSection])

  // ── Fetch ─────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!id) return
    getAdminSession(id)
      .then((data) => {
        setSession(data)
        if (data.sections.length > 0) setActiveSection(data.sections[0].key)
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false))
  }, [id])

  // ── Preload metadata for ALL sections immediately so bar widths are correct ──
  // Creates throwaway <video preload="metadata"> elements — no visible DOM change.

  useEffect(() => {
    if (!session) return
    session.sections.forEach((section) => {
      const probe = (url: string, side: 'user' | 'avatar') => {
        const vid = document.createElement('video')
        vid.preload = 'metadata'
        vid.src = url
        vid.onloadedmetadata = () => {
          const dur = vid.duration
          vid.src = '' // release
          if (!isFinite(dur) || dur <= 0) return
          setSectionDurs(prev => ({
            ...prev,
            [section.key]: {
              ...(prev[section.key] ?? { user: 0, avatar: 0 }),
              [side]: dur,
            },
          }))
        }
      }
      if (section.user_video_url)   probe(section.user_video_url,   'user')
      if (section.avatar_video_url) probe(section.avatar_video_url, 'avatar')
    })
  }, [session]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Derived: current section + duration helpers ───────────────────────────

  const sections       = session?.sections ?? []
  const activeIdx      = sections.findIndex(s => s.key === activeSection)
  const currentSection = sections.find(s => s.key === activeSection)

  const getEffectiveDur = (key: string) => {
    const d = sectionDurs[key]
    if (!d) return 0
    return Math.max(d.user, d.avatar)
  }

  const totalDuration = sections.reduce((sum, s) => sum + getEffectiveDur(s.key), 0)

  const absoluteCurrentTime =
    sections.slice(0, Math.max(0, activeIdx)).reduce((sum, s) => sum + getEffectiveDur(s.key), 0)
    + currentTime

  // ── Section-change effect — reload videos, optionally auto-play ───────────

  useEffect(() => {
    if (!currentSection) return
    setCurrentTime(0)
    advancedKeyRef.current = null

    const shouldAutoPlay = autoPlayNextRef.current
    autoPlayNextRef.current = false

    if (!shouldAutoPlay) setIsPlaying(false)

    const userVid   = candidateVideoRef.current
    const avatarVid = avatarVideoRef.current
    userVid?.load()
    avatarVid?.load()

    if (shouldAutoPlay) {
      userVid?.play().catch(() => {})
      avatarVid?.play().catch(() => {})
    }
  }, [currentSection?.key]) // eslint-disable-line react-hooks/exhaustive-deps

  // Keep primaryIsUserRef in sync whenever durations update
  useEffect(() => {
    if (!currentSection) return
    const d = sectionDurs[currentSection.key]
    if (d) primaryIsUserRef.current = d.user >= d.avatar
  }, [sectionDurs, currentSection])

  // ── Video ended → auto-advance to next section ────────────────────────────

  const handleVideoEnded = useCallback((sectionKey: string) => {
    // Guard: only advance once even if both videos fire ended
    if (advancedKeyRef.current === sectionKey) return
    advancedKeyRef.current = sectionKey

    const nextIdx = activeIdxRef.current + 1
    const secs    = sectionsRef.current
    if (nextIdx < secs.length) {
      autoPlayNextRef.current = true
      setActiveSection(secs[nextIdx].key)
    } else {
      setIsPlaying(false)
    }
  }, [])

  // ── Duration metadata update helpers ─────────────────────────────────────

  const onUserMeta = () => {
    const dur = candidateVideoRef.current?.duration ?? 0
    if (!currentSection || !isFinite(dur) || dur <= 0) return
    const key = currentSection.key
    setSectionDurs(prev => ({
      ...prev,
      [key]: { ...(prev[key] ?? { user: 0, avatar: 0 }), user: dur },
    }))
  }

  const onAvatarMeta = () => {
    const dur = avatarVideoRef.current?.duration ?? 0
    if (!currentSection || !isFinite(dur) || dur <= 0) return
    const key = currentSection.key
    setSectionDurs(prev => ({
      ...prev,
      [key]: { ...(prev[key] ?? { user: 0, avatar: 0 }), avatar: dur },
    }))
  }

  // ── Unified play / pause ──────────────────────────────────────────────────

  const togglePlayback = () => {
    const userVid   = candidateVideoRef.current
    const avatarVid = avatarVideoRef.current
    if (!userVid) return
    if (isPlaying) {
      userVid.pause()
      avatarVid?.pause()
      setIsPlaying(false)
    } else {
      userVid.play().catch(() => {})
      avatarVid?.play().catch(() => {})
      setIsPlaying(true)
    }
  }

  // ── Loading / error ───────────────────────────────────────────────────────

  if (loading) {
    return (
      <Shell>
        <LoadingSpinner size="lg" />
      </Shell>
    )
  }

  if (error || !session) {
    return (
      <Shell>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}>
          <p style={{ color: T.muted, fontFamily: T.font, fontSize: '14px' }}>
            Session nicht gefunden.
          </p>
          <GhostBtn onClick={() => navigate('/dashboard')}>← Dashboard</GhostBtn>
        </div>
      </Shell>
    )
  }

  // ── Derived display values ────────────────────────────────────────────────

  const { candidate } = session
  const candidateName = [candidate.first_name, candidate.last_name].filter(Boolean).join(' ') || '—'
  const dateStr = session.created_at
    ? new Date(session.created_at).toLocaleDateString('de-DE', {
        day: '2-digit', month: 'short', year: 'numeric',
      })
    : '—'

  return (
    <Shell>
      {/* ── Top bar ──────────────────────────────────────────────────────── */}
      <TopBar
        jobTitle={session.job_title}
        companyName={session.company_name}
        status={session.status}
        onBack={() => navigate('/dashboard')}
      />

      {/* ── Stage ────────────────────────────────────────────────────────── */}
      <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>

        {/* Candidate video — fullscreen background stage */}
        {currentSection?.user_video_url ? (
          <video
            key={currentSection.key + '_user'}
            ref={candidateVideoRef}
            src={currentSection.user_video_url}
            style={{
              position: 'absolute', inset: 0,
              width: '100%', height: '100%',
              objectFit: 'cover', zIndex: 0,
            }}
            onLoadedMetadata={onUserMeta}
            onTimeUpdate={() => {
              if (primaryIsUserRef.current && candidateVideoRef.current) {
                setCurrentTime(candidateVideoRef.current.currentTime)
              }
            }}
            onEnded={() => {
              if (primaryIsUserRef.current) handleVideoEnded(currentSection.key)
            }}
          />
        ) : (
          <div style={{
            position: 'absolute', inset: 0, zIndex: 0,
            background: 'linear-gradient(145deg, #0d1b2a 0%, #1a2a3a 50%, #0a1525 100%)',
          }} />
        )}

        {/* Dark overlay for readability of floating panels */}
        <div style={{
          position: 'absolute', inset: 0, zIndex: 1,
          background: 'linear-gradient(to right, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.1) 50%, rgba(0,0,0,0.55) 100%)',
          pointerEvents: 'none',
        }} />

        {/* ── Session details card — top-left ──────────────────────────── */}
        <div className="sdp-cv-left" style={{
          position: 'absolute', top: '24px', left: '36px',
          width: '300px', zIndex: 10,
        }}>
          <div style={glassCard}>
            <p style={glassLabel}>Interview-Details</p>
            <DetailRow label="Kandidat"    value={candidateName} bold />
            <DetailRow label="Position"    value={session.job_title    || '—'} bold />
            <DetailRow label="Unternehmen" value={session.company_name || '—'} />
            <DetailRow label="Datum"       value={dateStr} />
            <DetailRow label="Sprache"     value={session.language.toUpperCase()} />
            <DetailRow label="Avatar"      value={session.avatar_gender} />
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              paddingTop: '10px', marginTop: '8px',
              borderTop: '1px solid rgba(255,255,255,0.07)',
              fontFamily: T.font,
            }}>
              <span style={{ fontSize: '13px', color: T.muted }}>Status</span>
              <span style={{ fontSize: '13px', fontWeight: 600, color: T.accent }}>
                {session.status === 'completed' ? 'Abgeschlossen ✓' : session.status}
              </span>
            </div>
          </div>
        </div>

        {/* ── Transcript panel — top-right ─────────────────────────────── */}
        <div className="sdp-cv-right" style={{
          position: 'absolute', top: '24px', right: '32px', bottom: '24px',
          width: '380px', zIndex: 10,
        }}>
          <TranscriptPanel currentSection={currentSection} />
        </div>

        {/* ── Avatar PiP — bottom-left ─────────────────────────────────── */}
        <div className="sdp-pip" style={{
          position:             'absolute',
          bottom:               '24px',
          left:                 '36px',
          width:                '260px',
          height:               '195px',
          background:           'rgba(0,0,0,0.3)',
          backdropFilter:       'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          borderRadius:         T.rLg,
          border:               '1px solid rgba(255,255,255,0.1)',
          overflow:             'hidden',
          zIndex:               10,
          boxShadow:            T.shadow,
          display:              'flex',
          alignItems:           'center',
          justifyContent:       'center',
        }}>
          {currentSection?.avatar_video_url ? (
            <video
              key={currentSection.key + '_avatar'}
              ref={avatarVideoRef}
              src={currentSection.avatar_video_url}
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              onLoadedMetadata={onAvatarMeta}
              onTimeUpdate={() => {
                if (!primaryIsUserRef.current && avatarVideoRef.current) {
                  setCurrentTime(avatarVideoRef.current.currentTime)
                }
              }}
              onEnded={() => {
                if (!primaryIsUserRef.current) handleVideoEnded(currentSection.key)
              }}
            />
          ) : (
            <span style={{ color: T.subtle, fontSize: '12px', fontFamily: T.font }}>
              Kein Avatar-Video
            </span>
          )}
          {/* PiP label */}
          <div style={{
            position: 'absolute', bottom: '10px', left: '12px',
            fontSize: '11px', fontWeight: 500, color: 'white',
            background: 'rgba(0,0,0,0.45)', padding: '3px 10px',
            borderRadius: '7px', backdropFilter: 'blur(6px)',
            fontFamily: T.font,
          }}>
            KI-Avatar
          </div>
        </div>
      </div>

      {/* ── Playback bar ─────────────────────────────────────────────────── */}
      <PlaybackBar
        sections={sections}
        activeIdx={activeIdx}
        onSelect={(key) => setActiveSection(key)}
        isPlaying={isPlaying}
        onTogglePlayback={togglePlayback}
        sectionDurs={sectionDurs}
        currentTime={currentTime}
        absoluteCurrentTime={absoluteCurrentTime}
        totalDuration={totalDuration}
      />
    </Shell>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Shell
// ─────────────────────────────────────────────────────────────────────────────

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      position:      'fixed',
      inset:         0,
      display:       'flex',
      flexDirection: 'column',
      fontFamily:    T.font,
      background:    '#1a2a3a',
      overflow:      'hidden',
    }}>
      {children}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// TopBar
// ─────────────────────────────────────────────────────────────────────────────

function TopBar({
  jobTitle, companyName, status, onBack,
}: {
  jobTitle: string; companyName: string; status: string; onBack: () => void
}) {
  return (
    <div style={{
      display:              'flex',
      alignItems:           'center',
      gap:                  '12px',
      padding:              '10px 28px',
      background:           'rgba(0,0,0,0.42)',
      backdropFilter:       T.blur,
      WebkitBackdropFilter: T.blur,
      borderBottom:         '1px solid rgba(255,255,255,0.06)',
      zIndex:               50,
      flexShrink:           0,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
        <div style={{
          width: '28px', height: '28px', background: T.accent,
          borderRadius: '8px', display: 'flex', alignItems: 'center',
          justifyContent: 'center', color: 'white', fontSize: '13px', fontWeight: 700,
        }}>T</div>
        <span style={{ fontWeight: 600, fontSize: '14px', color: T.text, whiteSpace: 'nowrap' }}>
          Tembo
        </span>
      </div>

      <div style={{ flex: 1 }} />

      <div className="sdp-top-center" style={{
        display: 'flex', alignItems: 'center', gap: '8px',
        fontSize: '13px', color: T.muted,
        overflow: 'hidden', maxWidth: '40%',
      }}>
        <span style={{
          fontWeight: 600, color: T.text,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {jobTitle || 'Interview'}
        </span>
        {companyName && <>
          <span style={{ color: T.subtle, flexShrink: 0 }}>·</span>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {companyName}
          </span>
        </>}
      </div>

      <div style={{ flex: 1 }} />

      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexShrink: 0 }}>
        <span style={{ fontSize: '12px', color: T.muted, whiteSpace: 'nowrap', fontFamily: T.font }}>
          {status === 'completed' ? 'Abgeschlossen ✓' : status}
        </span>
        <GhostBtn onClick={onBack}>← Dashboard</GhostBtn>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// TranscriptPanel
// ─────────────────────────────────────────────────────────────────────────────

function TranscriptPanel({ currentSection }: { currentSection: SessionSection | undefined }) {
  const transcripts = currentSection?.transcripts ?? []

  return (
    <div style={{ ...glassCard, height: '100%', display: 'flex', flexDirection: 'column', padding: '22px 22px' }}>
      <p style={glassLabel}>Transkript — {currentSection?.label ?? '—'}</p>

      <div
        className="sdp-no-scrollbar"
        style={{
          flex: 1, minHeight: 0, overflowY: 'auto',
          display: 'flex', flexDirection: 'column', gap: '12px',
          maskImage: 'linear-gradient(to bottom, transparent 0%, black 6%, black 100%)',
          WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, black 6%, black 100%)',
          paddingTop: '8px',
        }}
      >
        {transcripts.length === 0 ? (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flex: 1, color: T.subtle, fontSize: '13px', fontFamily: T.font,
          }}>
            Kein Transkript verfügbar.
          </div>
        ) : (
          transcripts.map((t, i) => {
            const isKI = t.speaker === 'interviewer'
            return (
              <div key={i} style={{ display: 'flex', justifyContent: isKI ? 'flex-start' : 'flex-end' }}>
                <div style={{
                  maxWidth: '88%', padding: '10px 14px',
                  borderRadius: isKI ? '16px 16px 16px 4px' : '16px 16px 4px 16px',
                  fontSize: '13px', lineHeight: 1.6, color: T.text, fontFamily: T.font,
                  background: isKI ? 'rgba(0,173,239,0.10)' : 'rgba(255,255,255,0.07)',
                  border: isKI ? '1px solid rgba(0,173,239,0.14)' : '1px solid rgba(255,255,255,0.06)',
                }}>
                  <span style={{
                    display: 'block', fontSize: '10px', fontWeight: 600,
                    marginBottom: '4px', fontFamily: T.font,
                    color: isKI ? T.accent : 'rgba(167,139,250,1)',
                  }}>
                    {isKI ? 'Ki-Interviewer' : 'Kandidat/in'}
                  </span>
                  {t.content}
                </div>
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// PlaybackBar — proportional chapter timeline with real progress + times
// ─────────────────────────────────────────────────────────────────────────────

function PlaybackBar({
  sections, activeIdx, onSelect, isPlaying, onTogglePlayback,
  sectionDurs, currentTime, absoluteCurrentTime, totalDuration,
}: {
  sections:             SessionSection[]
  activeIdx:            number
  onSelect:             (key: string) => void
  isPlaying:            boolean
  onTogglePlayback:     () => void
  sectionDurs:          SectionDurs
  currentTime:          number
  absoluteCurrentTime:  number
  totalDuration:        number
}) {
  const getEffectiveDur = (key: string) => {
    const d = sectionDurs[key]
    return d ? Math.max(d.user, d.avatar) : 0
  }

  // Only switch to proportional mode once every section has a known duration.
  // Prevents early sections (width=100%) from crushing unloaded ones (width=0).
  const allLoaded = sections.length > 0 && sections.every(s => getEffectiveDur(s.key) > 0)

  return (
    <div style={{
      display:              'flex',
      alignItems:           'center',
      gap:                  '14px',
      padding:              '10px 28px 14px',
      background:           'rgba(0,0,0,0.42)',
      backdropFilter:       'blur(30px)',
      WebkitBackdropFilter: 'blur(30px)',
      borderTop:            '1px solid rgba(255,255,255,0.06)',
      flexShrink:           0,
      zIndex:               50,
    }}>
      {/* Play / Pause button */}
      <button
        onClick={onTogglePlayback}
        style={{
          width: '40px', height: '40px', borderRadius: '50%', border: 'none',
          background: T.accent, color: 'white', flexShrink: 0, cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 4px 14px rgba(0,173,239,0.28)',
          transition: 'transform 0.15s ease',
        }}
        aria-label={isPlaying ? 'Pause' : 'Play'}
      >
        {isPlaying ? (
          <svg width="14" height="14" viewBox="0 0 14 14" fill="white">
            <rect x="2" y="1" width="4" height="12" rx="1" />
            <rect x="8" y="1" width="4" height="12" rx="1" />
          </svg>
        ) : (
          <svg width="14" height="14" viewBox="0 0 14 14" fill="white">
            <path d="M3 1.5l9 5.5-9 5.5V1.5z" />
          </svg>
        )}
      </button>

      {/* Timeline area */}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '6px' }}>

        {/* ── Section bars row ─────────────────────────────────────────── */}
        <div style={{ display: 'flex', gap: '3px', alignItems: 'center' }}>
          {sections.length === 0 ? (
            <div style={{ flex: 1, height: '5px', background: 'rgba(255,255,255,0.10)', borderRadius: '3px' }} />
          ) : sections.map((s, i) => {
            const dur      = getEffectiveDur(s.key)
            const isDone   = i < activeIdx
            const isActive = i === activeIdx

            // Proportional width once durations are known, equal-width fallback
            const flexStyle: React.CSSProperties = allLoaded && dur > 0
              ? { flex: 'none', width: `${(dur / totalDuration * 100).toFixed(3)}%` }
              : { flex: 1 }

            // Fill percentage within this bar
            let fillPct = 0
            if (isDone) {
              fillPct = 100
            } else if (isActive && dur > 0) {
              fillPct = Math.min(100, (currentTime / dur) * 100)
            }

            return (
              <button
                key={s.key}
                onClick={() => onSelect(s.key)}
                title={s.label}
                style={{
                  ...flexStyle,
                  height:           isActive ? '7px' : '5px',
                  border:           'none',
                  padding:          0,
                  borderRadius:     '3px',
                  cursor:           'pointer',
                  position:         'relative',
                  overflow:         'hidden',
                  background:       'rgba(255,255,255,0.11)',
                  transition:       'height 0.12s ease',
                  outline:          isActive ? '2px solid rgba(0,173,239,0.35)' : 'none',
                  outlineOffset:    '2px',
                  flexShrink:       0,
                }}
              >
                {/* Fill — progress fill for this section */}
                <div style={{
                  position:         'absolute',
                  left:             0, top: 0, bottom: 0,
                  width:            `${fillPct}%`,
                  background:       isDone
                    ? T.accent
                    : `linear-gradient(90deg, ${T.accent} 0%, rgba(0,173,239,0.75) 100%)`,
                  borderRadius:     'inherit',
                  // Smooth progress animation only for active section
                  transition:       isActive ? 'width 0.25s linear' : 'none',
                  pointerEvents:    'none',
                }} />
              </button>
            )
          })}
        </div>

        {/* ── Labels + section durations row ───────────────────────────── */}
        <div style={{ display: 'flex', gap: '3px', alignItems: 'baseline' }}>
          {sections.map((s, i) => {
            const dur      = getEffectiveDur(s.key)
            const isActive = i === activeIdx

            const flexStyle: React.CSSProperties = allLoaded && dur > 0
              ? { flex: 'none', width: `${(dur / totalDuration * 100).toFixed(3)}%` }
              : { flex: 1 }

            return (
              <button
                key={s.key}
                onClick={() => onSelect(s.key)}
                style={{
                  ...flexStyle,
                  border:          'none',
                  background:      'transparent',
                  padding:         0,
                  fontFamily:      T.font,
                  cursor:          'pointer',
                  display:         'flex',
                  alignItems:      'baseline',
                  gap:             '3px',
                  overflow:        'hidden',
                  minWidth:        0,
                  flexShrink:      0,
                }}
              >
                <span style={{
                  fontSize:      '9px',
                  fontWeight:    isActive ? 600 : 400,
                  color:         isActive ? T.text : T.subtle,
                  whiteSpace:    'nowrap',
                  overflow:      'hidden',
                  textOverflow:  'ellipsis',
                  transition:    'color 0.2s ease',
                }}>
                  {s.label}
                </span>
                {dur > 0 && (
                  <span style={{
                    fontSize:   '8px',
                    color:      isActive ? T.muted : 'rgba(255,255,255,0.22)',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                    fontVariantNumeric: 'tabular-nums',
                    transition: 'color 0.2s ease',
                  }}>
                    {fmtTime(dur)}
                  </span>
                )}
              </button>
            )
          })}
        </div>
      </div>

      {/* Time display */}
      <div style={{
        display:        'flex',
        flexDirection:  'column',
        alignItems:     'flex-end',
        flexShrink:     0,
        gap:            '2px',
        minWidth:       '64px',
      }}>
        <span style={{
          fontSize:           '13px',
          fontWeight:         500,
          color:              T.text,
          fontFamily:         T.font,
          fontVariantNumeric: 'tabular-nums',
          letterSpacing:      '0.3px',
        }}>
          {fmtTime(absoluteCurrentTime)}
        </span>
        <span style={{
          fontSize:           '10px',
          color:              T.subtle,
          fontFamily:         T.font,
          fontVariantNumeric: 'tabular-nums',
        }}>
          {totalDuration > 0 ? `/ ${fmtTime(totalDuration)}` : '—'}
        </span>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Shared micro-components
// ─────────────────────────────────────────────────────────────────────────────

function DetailRow({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', padding: '5px 0',
      fontSize: '13px', fontFamily: T.font,
    }}>
      <span style={{ color: T.muted }}>{label}</span>
      <span style={{
        fontWeight: bold ? 500 : 400, color: T.text,
        overflow: 'hidden', textOverflow: 'ellipsis',
        whiteSpace: 'nowrap', maxWidth: '60%', textAlign: 'right',
      }}>
        {value}
      </span>
    </div>
  )
}

function GhostBtn({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '6px 14px', borderRadius: '10px',
        border: '1px solid rgba(255,255,255,0.10)',
        background: 'rgba(255,255,255,0.06)',
        color: T.muted, fontFamily: T.font,
        fontSize: '13px', fontWeight: 500,
        cursor: 'pointer', whiteSpace: 'nowrap',
        transition: 'all 0.15s ease',
      }}
    >
      {children}
    </button>
  )
}
